/**
 * The phone (docs/ux/loi.md, iPhone adaptation): a reading surface. The
 * state card, the form as cards, the questions, the standing instructions
 * and the letter behind a collapsible. No decisions, no Approve LOI.
 */

import { ChevronRightIcon } from 'lucide-react';
import { Suspense, lazy, useState } from 'react';
import type { LoiDiffRow, LoiView } from '@/app/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { StateChip } from '../casefile/ReviewBar';
import { DecisionMark } from '../casefile/ReviewQueue';
import { chipFor } from './LoiReviewBar';
import { DECISION_WORD, MACHINE_ABSENT, REVIEWED_ABSENT, SEP, blockingSentence, formatValue, settledValue, stateSentence, type Counts } from './describe';
import { letterPagesUrl } from './useLoi';

const PageRangeViewer = lazy(() => import('../casefile/PageRangeViewer'));

export function LoiStateCard({ view, counts, recorded, diff }: { view: LoiView; counts: Counts; recorded: boolean; diff: LoiDiffRow[] | undefined }) {
  const [changesOpen, setChangesOpen] = useState(false);
  const head = view.head;
  const approved = head.state === 'approved';
  const chip = chipFor(head, false);
  const changed = (diff ?? []).filter((r) => r.decision === 'corrected' || r.decision === 'missing');
  const byKey = new Map(view.fields.map((f) => [f.key, f]));
  return (
    <section aria-label="LOI review" data-slot="loi-state-card" className="flex flex-col gap-2 rounded-xl border border-beam-line bg-beam-paper p-4 text-sm">
      <div className="flex flex-wrap gap-2">
        {view.extraction ? <Badge variant="outline" className={recorded ? 'h-6 border-transparent bg-beam-warning-bg px-2.5 text-xs text-beam-warning' : 'h-6 px-2.5 text-xs'}>{recorded ? 'Recorded extraction' : 'Live extraction'}</Badge> : null}
        {view.extraction ? <StateChip label={chip.label} tone={chip.tone} attrs={{ 'data-review-state': head.state }} /> : <StateChip label="No extraction" tone="neutral" attrs={{ 'data-review-state': 'none' }} />}
      </div>
      {!view.extraction ? (
        <p>No extraction has been run. {view.letterPages ? `The letter (${view.letterPages} ${view.letterPages === 1 ? 'page' : 'pages'}) is on file.` : ''} Run the extraction from a desktop.</p>
      ) : approved ? (
        <>
          <p>{stateSentence(head, counts, false, false)}</p>
          <p>{diff === undefined ? 'Loading what changed from the machine\'s reading.' : `${changed.length} ${changed.length === 1 ? 'item' : 'items'} changed from the machine\'s reading.`}</p>
          {changed.length > 0 ? <Button variant="outline" size="lg" className="h-11 justify-start" aria-expanded={changesOpen} onClick={() => setChangesOpen((v) => !v)}>Show what changed</Button> : null}
          {changesOpen ? (
            <ul className="flex flex-col divide-y divide-beam-line text-[13px]">
              {changed.map((r) => { const f = r.kind === 'field' ? byKey.get(r.key) : undefined; return (
                <li key={`${r.kind}-${r.key}`} className="flex flex-col gap-0.5 py-2">
                  <span className="font-medium">{r.kind === 'field' ? r.label : `Question ${r.label}`}</span>
                  <span className="text-beam-text-muted">Machine: {f ? formatValue(f, r.machine, MACHINE_ABSENT) : String(r.machine ?? MACHINE_ABSENT)}</span>
                  <span>Reviewed: {f ? formatValue(f, r.reviewed, REVIEWED_ABSENT) : String(r.reviewed ?? REVIEWED_ABSENT)}</span>
                  <DecisionMark word={DECISION_WORD[r.decision]} />
                </li>
              ); })}
            </ul>
          ) : null}
        </>
      ) : (
        <>
          <p>{stateSentence(head, counts, false, view.fields.some((f) => f.item?.prior))}</p>
          <p>{blockingSentence(counts, head.authoritativeVersion)}</p>
          <p className="text-[13px] text-beam-text-muted">Fields and questions are decided and the LOI approved on a desktop.</p>
        </>
      )}
    </section>
  );
}

export function FieldCards({ view }: { view: LoiView }) {
  const approved = view.head.state === 'approved';
  const groups = [...new Set(view.fields.map((f) => f.group))];
  return (
    <>
      {groups.map((g) => (
        <section key={g} aria-label={view.groups[g] ?? g} className="flex flex-col gap-2">
          <h3 className="eyebrow px-1 text-beam-text-muted"><span className="mr-2 text-foreground">{g}</span>{view.groups[g] ?? g}</h3>
          <div className="flex flex-col divide-y divide-beam-line rounded-xl border border-beam-line bg-beam-paper">
            {view.fields.filter((f) => f.group === g).map((f) => {
              const decision = f.item?.decision ?? 'unresolved';
              const value = approved ? view.authoritative?.fields[f.key] ?? null : settledValue(f);
              return (
                <article key={f.key} data-key={f.key} className="flex min-h-11 flex-col gap-0.5 px-4 py-3">
                  <span className="text-xs text-beam-text-muted">{f.label}{f.required ? `${SEP}Required` : ''}</span>
                  {approved ? (
                    <>
                      <span className={value === null ? 'text-[16px] text-beam-text-muted' : 'text-[16px]'}>{formatValue(f, value, REVIEWED_ABSENT)}{decision === 'corrected' || decision === 'missing' ? <span className="ml-2 text-xs text-beam-text-muted">{decision}</span> : null}</span>
                      {(decision === 'corrected' || decision === 'missing') && f.machine ? <span className="text-xs text-beam-text-muted">Machine read: {formatValue(f, f.machine.value, MACHINE_ABSENT)}</span> : null}
                    </>
                  ) : (
                    <>
                      <span className="text-[13px] text-beam-text-muted">Machine: {f.machine ? formatValue(f, f.machine.value, MACHINE_ABSENT) : 'No extraction'}</span>
                      <span className="text-[16px]">Reviewed: {decision === 'unresolved' || decision === 'flagged' ? <span className="text-beam-text-muted">Not decided</span> : formatValue(f, value, REVIEWED_ABSENT)}</span>
                      <DecisionMark word={DECISION_WORD[decision]} />
                    </>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}

export function QuestionCards({ view }: { view: LoiView }) {
  const approved = view.head.state === 'approved';
  return (
    <section aria-label="Questions" className="flex flex-col gap-2">
      <h3 className="eyebrow px-1 text-beam-text-muted"><span className="mr-2 text-foreground">G</span>Questions</h3>
      <div className="flex flex-col divide-y divide-beam-line rounded-xl border border-beam-line bg-beam-paper">
        {view.questions.map((q) => (
          <article key={q.id} data-id={q.id} className="flex min-h-11 flex-col gap-1 px-4 py-3" style={{ paddingLeft: 16 + q.depth * 16 }}>
            <p className="text-[16px] leading-[1.45]"><span className="tabular mr-2 font-semibold">{q.label}</span>{q.item?.decision === 'corrected' ? q.item.reviewedText : q.text}</p>
            {!approved ? <DecisionMark word={DECISION_WORD[q.item?.decision ?? 'unresolved']} /> : q.item?.decision === 'corrected' ? <span className="text-xs text-beam-text-muted">corrected</span> : null}
          </article>
        ))}
      </div>
      {view.standingInstructions.length > 0 ? (
        <>
          <h3 className="eyebrow mt-2 px-1 text-beam-text-muted">Standing instructions <span className="tabular">{view.standingInstructions.length}</span></h3>
          <ul className="flex flex-col divide-y divide-beam-line rounded-xl border border-beam-line bg-beam-paper">{view.standingInstructions.map((s, i) => <li key={i} className="flex min-h-11 items-center justify-between gap-3 px-4 py-3 text-[16px]"><span>{s.text}</span>{s.sourcePage ? <span className="text-xs text-beam-text-muted">Page {s.sourcePage}</span> : null}</li>)}</ul>
        </>
      ) : null}
    </section>
  );
}

export function LetterCollapsible({ matterRef, pages }: { matterRef: string; pages: number }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="group flex min-h-11 w-full items-center gap-2 rounded-xl border border-beam-line bg-beam-paper px-4 text-sm font-medium"><ChevronRightIcon aria-hidden="true" className="size-4 transition-transform group-data-[state=open]:rotate-90" /> Letter, {pages} {pages === 1 ? 'page' : 'pages'}</CollapsibleTrigger>
      <CollapsibleContent>
        {open ? (
          <div className="mt-2">
            <Suspense fallback={<Skeleton className="aspect-[595/842] w-full rounded-xl" />}>
              <PageRangeViewer matterRef={matterRef} runId="letter" pagesUrl={(from, to) => letterPagesUrl(matterRef, from, to)} subject="the letter" plan={{ mode: 'range', windows: [{ from: 1, to: Math.min(pages, 12) }], extent: { from: 1, to: pages }, captions: {}, dashedBefore: [] }} yourBoundary={null} phone />
            </Suspense>
          </div>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}
